import { FC, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import BasicModal from '__components/Modals/presets/BasicModal';
import TextInput from '__components/Inputs/TextInput';
import LongText from '__components/Inputs/LongText';
import Switch from '__components/Kizen/Switch';
import InputControl from '__components/Inputs/InputControl';
import PermissionSettings from '__components/Sharing/PermissionSettings';
import { buildSharingSettingsForAPI } from 'utility/sharing';
import type { KnowledgeCatalogSharingSettings } from '@kizen/api/knowledge';

const StyledFieldWrapper = styled.div<{ marginTop?: string }>`
  margin-top: ${({ marginTop }) => marginTop || '15px'};
  text-align: start;
`;

type NewCatalogModalProps = {
  onConfirm: (
    name: string,
    description: string,
    sharingSettings: Record<string, unknown>,
    isPublishable: boolean
  ) => Promise<void>;
  onHide: () => void;
};

export const NewCatalogModal: FC<NewCatalogModalProps> = ({
  onConfirm,
  onHide,
}) => {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isPublishable, setIsPublishable] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [permissions, setPermissions] = useState<any>(null);

  const handleConfirm = useCallback(async () => {
    if (!name.trim()) return;
    setSubmitting(true);
    try {
      const sharingSettings =
        (
          buildSharingSettingsForAPI(permissions) as {
            sharing_settings?: KnowledgeCatalogSharingSettings;
          }
        )?.sharing_settings ?? {};
      await onConfirm(
        name.trim(),
        description.trim(),
        sharingSettings,
        isPublishable
      );
    } finally {
      setSubmitting(false);
    }
  }, [name, description, permissions, isPublishable, onConfirm]);

  return (
    <BasicModal
      heading={t('New Knowledge Catalog')}
      buttonText={t('Create')}
      defaultLeftBtnText={t('Cancel')}
      disabled={!name.trim() || submitting}
      show
      size="medium"
      onConfirm={handleConfirm}
      onHide={onHide}
    >
      <StyledFieldWrapper marginTop="0">
        <TextInput
          label={t('Name')}
          placeholder={t('Enter catalog name')}
          value={name}
          onChange={setName}
          required
          data-qa-id="new-catalog-name-input"
        />
      </StyledFieldWrapper>
      <StyledFieldWrapper>
        <LongText
          label={t('Description')}
          placeholder={t('Enter description (optional)')}
          value={description}
          onChange={setDescription}
          data-qa-id="new-catalog-description-input"
        />
      </StyledFieldWrapper>
      <StyledFieldWrapper>
        <InputControl margin label={t('Publish to Marketplace')}>
          <Switch
            checked={isPublishable}
            onChange={() => setIsPublishable((prev) => !prev)}
            removeMargin
            dataQa="new-catalog-publishable-toggle"
          />
        </InputControl>
      </StyledFieldWrapper>
      <PermissionSettings
        onChange={setPermissions}
        existing={{}}
        disableFocus
        removeTopPadding
      />
    </BasicModal>
  );
};
